import type { Field } from '@/api/types/crm/crm.types'
import type { KanbanFilters } from '../store/kanban.types'

export type KanbanFieldFilterRange = {
  from?: string
  to?: string
}

export type KanbanFieldFilterValue =
  | string
  | string[]
  | number[]
  | KanbanFieldFilterRange

export const TRAFFIC_CHANNEL_FIELD_UNIQUE_NAME = 'TRAFFIC_CHANNEL'

export const isTrafficChannelKanbanField = (
  field: Pick<Field, 'unique_name'>,
): boolean => field.unique_name === TRAFFIC_CHANNEL_FIELD_UNIQUE_NAME

export const isKanbanFieldFilterValueEmpty = (
  value: KanbanFieldFilterValue | undefined | null,
): boolean => {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  const from = value.from?.trim() ?? ''
  const to = value.to?.trim() ?? ''
  return !from && !to
}

const normalizeKanbanFieldFilterEntry = (
  value: KanbanFieldFilterValue | null | undefined,
): KanbanFieldFilterValue | null => {
  if (value == null || isKanbanFieldFilterValueEmpty(value)) return null
  if (typeof value === 'string') {
    return value.trim()
  }
  if (Array.isArray(value)) return value

  const from = value.from?.trim() ?? ''
  const to = value.to?.trim() ?? ''
  if (!from && !to) return null
  return {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }
}

export const normalizeKanbanFieldFilters = (
  fieldFilters?: Record<string, KanbanFieldFilterValue> | null,
): Record<string, KanbanFieldFilterValue> | undefined => {
  if (!fieldFilters) return undefined
  const next: Record<string, KanbanFieldFilterValue> = {}
  for (const [key, value] of Object.entries(fieldFilters)) {
    const normalized = normalizeKanbanFieldFilterEntry(value)
    if (normalized) {
      next[key] = normalized
    }
  }
  return Object.keys(next).length > 0 ? next : undefined
}

export const serializeKanbanFieldFilters = (
  fieldFilters?: Record<string, KanbanFieldFilterValue> | null,
): string => {
  const normalized = normalizeKanbanFieldFilters(fieldFilters) ?? {}
  const sortedKeys = Object.keys(normalized).sort((a, b) => Number(a) - Number(b))
  const sorted = sortedKeys.reduce<Record<string, KanbanFieldFilterValue>>(
    (acc, key) => {
      acc[key] = normalized[key]
      return acc
    },
    {},
  )
  return JSON.stringify(sorted)
}

export const pickKanbanUrlFilters = (filters: KanbanFilters) => ({
  managerId: filters.managerId ?? null,
  nextContactDateFrom: filters.nextContactDateFrom ?? null,
  nextContactDateTo: filters.nextContactDateTo ?? null,
  lastActivityDateFrom: filters.lastActivityDateFrom ?? null,
  lastActivityDateTo: filters.lastActivityDateTo ?? null,
  source: filters.source ?? null,
  overdueOnly: filters.overdueOnly ?? false,
})

export const appendKanbanFieldFiltersToSearchParams = (
  query: URLSearchParams,
  fieldFilters: Record<string, KanbanFieldFilterValue>,
) => {
  Object.entries(fieldFilters).forEach(([fieldId, value]) => {
    if (isKanbanFieldFilterValueEmpty(value)) return

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed) {
        query.set(`filters[field_filters][${fieldId}]`, trimmed)
      }
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || item === '') return
        query.append(`filters[field_filters][${fieldId}][]`, String(item))
      })
      return
    }

    if (value.from) {
      query.set(`filters[field_filters][${fieldId}][from]`, String(value.from))
    }
    if (value.to) {
      query.set(`filters[field_filters][${fieldId}][to]`, String(value.to))
    }
  })
}
